import React, { useState, useEffect } from "react";
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { useTranslation } from "react-i18next";
import { getAllOrder } from "../API/order";
import { showErrorToast } from "../components/common";
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Close from './../assets/images/close_icon.svg';
import { FaUtensils, FaSun, FaCloudSun, FaMoon } from 'react-icons/fa';
import Header from "../components/Common/Header";
import { ButtonWithIcon } from "../components/Common/Buttons";
import { cn } from "../lib/utils";

// Setup the localizer by providing the moment (or globalize) Object
// to the correct localizer.
const localizer = momentLocalizer(moment);

const NewDashboard = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Fetch orders and convert them to calendar events
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 3); // Get orders from last 3 months
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 3); // Get orders for next 3 months

      const response = await getAllOrder({
        startDate,
        endDate,
        selectedStatus: '' // Get all statuses
      });

      if (response?.data?.success) {
        const orderEvents = response.data.data.map((order) => {
          // Create event based on order event date or creation date
          let eventDate;
          if (order.eventDate) {
            eventDate = new Date(order.eventDate);
          } else {
            eventDate = new Date(order.createdAt);
          }

          // Ensure the date is valid
          if (isNaN(eventDate.getTime())) {
            eventDate = new Date(order.createdAt);
          }

          // Set start time to beginning of day
          eventDate.setHours(9, 0, 0, 0); // 9 AM start time

          // Create end date (same day but with a duration for better calendar display)
          const endDate = new Date(eventDate);
          endDate.setHours(18, 0, 0, 0); // 6 PM end time

          // Determine event color based on order status (handle numeric status)
          let eventColor = '#3174ad'; // Default blue
          let statusText = 'unknown';

          // Handle numeric status values based on your statusList
          switch (order.status) {
            case 1:
              eventColor = ' #FF0000'; // Yellow/Orange for In Progress
              statusText = 'inprogress';
              break;
            case 2:
              eventColor = '#27ae60'; // Green for Completed
              statusText = 'completed';
              break;
            case 3:
              eventColor = '#3498db'; // Blue for Upcoming
              statusText = 'upcoming';
              break;
            case 4:
              eventColor = '#f39c12'; // Red for Deleted
              statusText = 'deleted';
              break;
            default:
              eventColor = '#3174ad'; // Default blue
              statusText = 'unknown';
          }

          // Get customer name from partyName
          const customerName = order.partyName || 'Customer';

          return {
            id: order._id,
            title: `Order #${order.order_id || order._id.slice(-6)} - ${customerName}`,
            start: eventDate,
            end: endDate,
            resource: order,
            color: eventColor,
            status: statusText,
            statusNumber: order.status,
            totalAmount: order.totalAmount || 0,
            allDay: false // Explicitly set to false for better time-based display
          };
        });

        setEvents(orderEvents);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      showErrorToast(t('error_fetching_orders'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // useEffect(() => {
  //   console.log('Current view updated to:', currentView);
  // }, [currentView]);

  // Custom event component
  const EventComponent = ({ event }) => (
    <div
      className="p-1 text-xs rounded cursor-pointer"
      style={{
        backgroundColor: event.color,
        color: 'white',
        fontWeight: 'bold'
      }}
      title={`${event.title}\nStatus: ${event.status}\nAmount: ₹${event.totalAmount}\nFarm: ${event.resource?.farm?.farmName || 'N/A'}`}
    >
      <div className="truncate">{event.title}</div>
    </div>
  );

  // Handle event selection
  const handleSelectEvent = (event) => {
    setSelectedOrder(event.resource);
    setIsModalOpen(true);
  };

  // Handle date selection
  const handleSelectSlot = ({ start, end }) => {
    console.log('Selected slot:', { start, end });
    // You can add logic to create new orders for selected date
  };

  // Handle view change
  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  // Custom toolbar component
  const CustomToolbar = ({ label, onNavigate, onView, view }) => (
    <div className="flex justify-between items-center mb-4 p-4 bg-white rounded-lg shadow-sm">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => onNavigate('PREV')}
          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          ← {t('previous')}
        </button>
        <button
          onClick={() => onNavigate('TODAY')}
          className="px-3 py-2 bg-primary text-white hover:bg-primary-dark rounded-md transition-colors"
        >
          {t('today')}
        </button>
        <button
          onClick={() => onNavigate('NEXT')}
          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          {t('next')} →
        </button>
      </div>

      <h2 className="text-xl font-bold text-gray-800">{label}</h2>

      <div className="flex items-center space-x-2">
        <button
          onClick={() => {
            onView('month');
            handleViewChange('month');
          }}
          className={`px-3 py-2 rounded-md transition-colors ${view === 'month' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'
            }`}
        >
          {t('month')}
        </button>
        <button
          onClick={() => {
            onView('week');
            handleViewChange('week');
          }}
          className={`px-3 py-2 rounded-md transition-colors ${view === 'week' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'
            }`}
        >
          {t('week')}
        </button>
        <button
          onClick={() => {
            onView('day');
            handleViewChange('day');
          }}
          className={`px-3 py-2 rounded-md transition-colors ${view === 'day' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'
            }`}
        >
          {t('day')}
        </button>
      </div>
    </div>
  );

  // Order status legend
  const StatusLegend = () => (
    <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('order_status_legend')}</h3>
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f39c12' }}></div>
          <span className="text-sm text-gray-700">{t('inprogress')}</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#27ae60' }}></div>
          <span className="text-sm text-gray-700">{t('completed')}</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#3498db' }}></div>
          <span className="text-sm text-gray-700">{t('upcoming')}</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#e74c3c' }}></div>
          <span className="text-sm text-gray-700">{t('deleted')}</span>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="px-5">
        <div className="pt-6 text-[30px] text-primary font-bold mb-6">
          {t('calendar_dashboard')}
        </div>

        <div className="space-y-6 animate-pulse">
          {/* Toolbar skeleton */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <div className="h-9 w-20 bg-gray-200 rounded"></div>
                <div className="h-9 w-20 bg-gray-200 rounded"></div>
                <div className="h-9 w-20 bg-gray-200 rounded"></div>
              </div>
              <div className="h-6 w-48 bg-gray-200 rounded"></div>
              <div className="flex gap-2">
                <div className="h-9 w-16 bg-gray-200 rounded"></div>
                <div className="h-9 w-16 bg-gray-200 rounded"></div>
                <div className="h-9 w-16 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>

          {/* Calendar skeleton */}
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: 35 }).map((_, idx) => (
                <div key={idx} className="h-20 bg-gray-100 rounded"></div>
              ))}
            </div>
          </div>

          {/* Stats skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="h-4 w-24 bg-gray-200 rounded"></div>
                    <div className="h-6 w-16 bg-gray-200 rounded"></div>
                  </div>
                  <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }


  const InfoCard = ({ icon, label, value, className }) => (
    <div className={`bg-gradient-to-br p-4 rounded-xl border ${className}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{icon}</span>
        <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
          {label}
        </span>
      </div>
      <p className="text-lg font-bold text-gray-900">{value || 'N/A'}</p>
    </div>
  );

  const Financialcard = ({ label, value, className, valueClass }) => (
    <div className={cn('rounded-xl border-2 bg-white p-4 shadow-sm', className)}>
      <div className="mb-1 text-sm font-semibold text-gray-600">{label}</div>
      <div className={cn("text-2xl font-bold", valueClass)}>₹{value}</div>
    </div>
  );

  const MenuSection = ({ Icon, iconClass, title, total, persons, items = [], border, totalKey }) => (
    <div className={`rounded-xl border ${border} bg-white p-4 shadow-sm`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`text-2xl ${iconClass}`} />}
          <h4 className="font-semibold text-gray-800">{title}</h4>
          {persons && (
            <span className="ml-2 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">
              {persons} {t('persons')}
            </span>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs font-semibold text-gray-500">Total</div>
          <div className="text-lg font-bold text-green-600">₹{total || 0}</div>
        </div>
      </div>

      {items.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {items.map((i, idx) => (
            <span key={idx} className="text-gray-700 rounded-full bg-gray-100 px-3 py-1 text-sm">
              {i?.menuName || '—'}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  const StatCard = ({ label, value, icon, color,textColor }) => (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <p className={`text-2xl font-bold ${textColor}`}>{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color.replace('text', 'bg')}`}>
          <span className="text-white text-xl">{icon}</span>
        </div>
      </div>
    </div>
  );

  const STATUS_CONFIG = {
    1: { label: 'inprogress', class: 'bg-yellow-100 text-yellow-800' },
    2: { label: 'completed', class: 'bg-green-100 text-green-800' },
    3: { label: 'upcoming', class: 'bg-blue-100 text-blue-800' },
    4: { label: 'deleted', class: 'bg-red-100 text-red-800' },
  };


  return (
    <div className="px-6">
      <Header title={t('calendar_dashboard')} className='pt-6 mb-6 ' titleClass='text-primary' />
      {/* <div className="pt-6 text-[30px] text-primary font-bold mb-6">
        {t('calendar_dashboard')}
      </div> */}

      {/* Calendar */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          onView={handleViewChange}
          view={currentView}
          onNavigate={(date) => setSelectedDate(date)}
          date={selectedDate}
          selectable
          step={60} // 60 minutes intervals
          timeslots={1} // Show 1 timeslot per hour
          min={new Date(2024, 0, 1, 6, 0)} // Start at 6 AM
          max={new Date(2024, 0, 1, 22, 0)} // End at 10 PM
          components={{
            toolbar: CustomToolbar,
            event: EventComponent
          }}
          eventPropGetter={(event) => ({
            style: {
              backgroundColor: event.color,
              borderRadius: '4px',
              opacity: 0.8,
              color: 'white',
              border: 'none',
              fontSize: '12px',
              fontWeight: 'bold'
            }
          })}
          messages={{
            next: t('next'),
            previous: t('previous'),
            today: t('today'),
            month: t('month'),
            week: t('week'),
            day: t('day'),
            agenda: t('agenda'),
            date: t('date'),
            time: t('time'),
            event: t('event'),
            noEventsInRange: t('no_events_in_range'),
            showMore: (total) => `+${total} ${t('more')}`
          }}
        />
      </div>

      {/* Quick Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard label={t('total_orders')} value={events.length} icon="📦" color=" bg-blue-500" textColor="text-gray-900" />

        <StatCard label={t('inprogress_orders')} value={events.filter(e => e.statusNumber === 1 || e.status === 'inprogress').length} icon="⏳" color=" bg-yellow-500" textColor="text-yellow-600" />

        <StatCard label={t('completed_orders')} value={events.filter(e => e.statusNumber === 2 || e.status === 'completed').length} icon="🎉" color="bg-green-500" textColor="text-green-600" />

        <StatCard label={t('upcoming_orders')} value={events.filter(e => e.statusNumber === 3 || e.status === 'upcoming').length} icon="📅" color="bg-blue-500" textColor="text-blue-600" />
      </div>

      {/* Order Details Modal */}
      {isModalOpen && selectedOrder && (
        () => {
          const details = selectedOrder?.OrderDetails?.[0] || {};
          const status = STATUS_CONFIG[selectedOrder.status] || {};
          const hasBreakfast = Boolean(details.bName || details.bTotalPerson || details.bTotal);
          const hasMorning = Array.isArray(details.morningMenuList) && details.morningMenuList.length > 0;
          const hasAfternoon = Array.isArray(details.afternoonMenuList) && details.afternoonMenuList.length > 0;
          const hasNight = Array.isArray(details.nightMenuList) && details.nightMenuList.length > 0;
          const hasAnyMenus = hasBreakfast || hasMorning || hasAfternoon || hasNight;

          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setIsModalOpen(false)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {/* Header with Gradient */}
                <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                        <span className="text-2xl">📋</span>
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold">{t('order_details')}</h2>
                        <p className="text-sm text-white text-opacity-90">
                          {t('id')}: {selectedOrder.order_id || selectedOrder._id?.slice(-6) || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <button
                      className="w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg flex items-center justify-center transition-all backdrop-blur-sm"
                      onClick={() => setIsModalOpen(false)}
                    >
                      <img src={Close} alt="close" className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                  {/* Status Badge */}
                  <div className="mb-6">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${status.class}`}>
                      <span className="w-2 h-2 rounded-full bg-current"></span>
                      {t(status.label || 'unknown')}
                    </div>
                  </div>

                  {/* Order Info Cards */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <InfoCard icon="👤" label={t('party_name')} value={selectedOrder.partyName} className="from-blue-50 to-blue-100 border-blue-200" />

                    <InfoCard icon="📞" label={t('contact_number')} value={selectedOrder.contactNumber || 'N/A'} className="from-teal-50 to-teal-100 border-teal-200" />

                    <InfoCard icon="🏡" label={t('farm')} value={selectedOrder.farm?.farmName || 'N/A'} className="bg-gradient-to-br from-green-50 to-green-100 border-green-200" />

                    <InfoCard icon="📍" label={t('location')} value={selectedOrder.farm?.address || selectedOrder.address || 'N/A'} className="from-purple-50 to-purple-100 border-purple-200" />

                    <InfoCard icon="📅" label={t('event_date')} value={selectedOrder.eventDate ? moment(selectedOrder.eventDate).format('DD-MM-YYYY') : 'N/A'} className="from-pink-50 to-pink-100 border-pink-200" />

                    <InfoCard icon="💰" label={t('Total_Amount')} value={`₹${selectedOrder?.OrderDetails[0]?.totalAmount || 0}`} className="from-orange-50 to-orange-100 border-orange-200" />

                  </div>

                  {/* Menu Details Section - Redesigned Layout */}
                  <div className="border-t-2 border-dashed border-gray-300 my-6"></div>
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <span className="text-2xl">🍽️</span>
                      {t('menu_details')}
                    </h3>

                    {!hasAnyMenus && (
                      <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
                        {t('no_menu_items')}
                      </div>
                    )}
                    <div className="space-y-4">
                      {details.bName && (
                        <MenuSection
                          Icon={FaUtensils}
                          iconClass="text-amber-600"
                          title={t('breakfast')}
                          total={details.bTotal}
                          persons={details.bTotalPerson}
                          border="border-amber-200"
                        />
                      )}

                      {details.morningMenuList?.length > 0 && (
                        <MenuSection
                          Icon={FaSun}
                          title={t('morning')}
                          iconClass="text-yellow-500"
                          total={details.mTotal}
                          persons={details.mTotalPerson}
                          items={details.morningMenuList}
                          border="border-yellow-200"
                        />
                      )}

                      {details.afternoonMenuList?.length > 0 && (
                        <MenuSection
                          Icon={FaCloudSun}
                          iconClass="text-orange-500"
                          title={t('afternoon')}
                          total={details.aTotal}
                          persons={details.aTotalPerson}
                          items={details.afternoonMenuList}
                          border="border-green-200"
                        />
                      )}

                      {details.nightMenuList?.length > 0 && (
                        <MenuSection
                          Icon={FaMoon}
                          iconClass="text-indigo-500"
                          title={t('night')}
                          total={details.nTotal}
                          persons={details.nTotalPerson}
                          items={details.nightMenuList}
                          border="border-indigo-200"
                        />
                      )}
                    </div>
                  </div>

                  {/* Financial Summary */}
                  <div className="border-t-2 border-dashed border-gray-300 my-6"></div>

                  <div className="rounded-xl border-2 border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 p-6">
                    <h4 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-800">
                      <span className="text-2xl">💳</span>
                      {t('financial_summary')}
                    </h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <Financialcard label={t('Total_Amount')} value={details.totalAmount || 0} className="border-green-300" valueClass="text-green-600" />
                      <Financialcard label={t('paid_by_client')} value={details.paidByClient || 0} className="border-blue-300" valueClass="text-blue-600" />
                      <Financialcard label={t('due_amount')} value={(details.totalAmount || 0) - (details.paidByClient || 0)} className="border-red-300" valueClass="text-red-600" />

                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
                  <ButtonWithIcon variant="secondary" text={t('close')} btnclass="px-6 bg-gradient-to-r from-blue-600 to-purple-600 font-medium hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl" onClick={() => setIsModalOpen(false)} />
                </div>
              </div>
            </div>
          )
        }
      )()}
    </div>
  );
};

export default NewDashboard;
